import { useId, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { describeError } from '@/features/auth/api';
import type { Site } from '@/features/sites/api';
import {
  useRemoveCasinoAccount,
  useSaveCasinoAccount,
  type CasinoAccount,
} from '../casino-accounts-api';

interface CasinoAccountFormProps {
  site: Site;
  saved: CasinoAccount | undefined;
  /** Collapses the row that opened this. */
  onSaved: () => void;
}

/**
 * The two fields that link a leaderboard row to this member, for one site.
 *
 * Both are optional and one of them is required, which is unusual enough to be
 * worth stating on screen rather than only in a validation error: partners
 * disagree about what their exports contain, so demanding both would lock out
 * the members of half of them.
 *
 * The inputs are seeded from what is saved and reseeded whenever that changes,
 * so a save made elsewhere shows up here — without a remount, which would throw
 * away whatever the user is halfway through typing.
 */
export function CasinoAccountForm({ site, saved, onSaved }: CasinoAccountFormProps) {
  const save = useSaveCasinoAccount();
  const remove = useRemoveCasinoAccount();
  const usernameId = useId();
  const externalIdId = useId();

  const savedUsername = saved?.username ?? '';
  const savedExternalId = saved?.externalUserId ?? '';

  const [username, setUsername] = useState(savedUsername);
  const [externalUserId, setExternalUserId] = useState(savedExternalId);

  // Reseeded DURING RENDER rather than in an effect.
  //
  // React's documented way to adjust state when a prop changes: it re-renders
  // this component immediately, before anything reaches the screen, so nobody
  // sees the stale values. The effect version renders once with the old text,
  // then again with the new — a visible flash on a slow phone, and a cascading
  // render on every one.
  const [seed, setSeed] = useState({ username: savedUsername, externalUserId: savedExternalId });

  if (seed.username !== savedUsername || seed.externalUserId !== savedExternalId) {
    setSeed({ username: savedUsername, externalUserId: savedExternalId });
    setUsername(savedUsername);
    setExternalUserId(savedExternalId);
  }

  const trimmedUsername = username.trim();
  const trimmedExternalId = externalUserId.trim();

  const isEmpty = trimmedUsername === '' && trimmedExternalId === '';
  const isUnchanged = trimmedUsername === savedUsername && trimmedExternalId === savedExternalId;

  const pending = save.isPending || remove.isPending;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (isEmpty || isUnchanged) {
      return;
    }

    save.mutate(
      {
        siteSlug: site.slug,
        username: trimmedUsername === '' ? null : trimmedUsername,
        externalUserId: trimmedExternalId === '' ? null : trimmedExternalId,
      },
      // Only on success: a conflict has to stay on screen, next to the field
      // that caused it.
      { onSuccess: onSaved },
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={usernameId} className="text-xs font-medium text-surface-400">
            Kullanıcı adı
          </label>
          <input
            id={usernameId}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            maxLength={64}
            placeholder="örn. CryptoKing"
            className="rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 outline-none transition placeholder:text-surface-600 focus:border-brand-500"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor={externalIdId} className="text-xs font-medium text-surface-400">
            Kullanıcı ID
          </label>
          <input
            id={externalIdId}
            value={externalUserId}
            onChange={(event) => setExternalUserId(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            maxLength={64}
            placeholder="örn. 8412337"
            className="rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 outline-none transition placeholder:text-surface-600 focus:border-brand-500"
          />
        </div>
      </div>

      <p className="text-xs text-surface-500">
        İkisi de zorunlu değil, ama en az birini girmelisin. {site.name} üzerindeki kullanıcı adın
        veya ID&apos;n neyse onu yaz — yarışmalarda seni bununla eşleştiriyoruz.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" loading={save.isPending} disabled={pending || isEmpty || isUnchanged}>
          Kaydet
        </Button>

        {saved ? (
          <Button
            type="button"
            variant="ghost"
            loading={remove.isPending}
            disabled={pending}
            onClick={() => remove.mutate(site.slug, { onSuccess: onSaved })}
          >
            Bağlantıyı kaldır
          </Button>
        ) : null}
      </div>

      {save.isError ? <Alert tone="error">{describeError(save.error)}</Alert> : null}
      {remove.isError ? <Alert tone="error">{describeError(remove.error)}</Alert> : null}
    </form>
  );
}
